import {
  CreditPlanFailureReason,
  CreditPlanResponseModel,
  CreditPlanStatus,
  UserTrackerData
} from '@/@types/client-api';
import { ICreditPlanBlockProps } from '@/components/blocks/CreditPlan/CreditPlanBlock';
import ExecutingCreditPlanSidePanel from '@/components/ui/CreditPlan/Panels/ExecutingCreditPlanSidePanel';
import GeneratedCreditPlanSidePanel from '@/components/ui/CreditPlan/Panels/GeneratedCreditPlanSidePanel';
import { useGetLicenseData } from '@/components/ui/LicensesPage/hooks';
import { useCallback, useMemo, useState } from 'react';
import { creditPlanContext } from './context';
import { useGetCreditPlan, useGetCreditPlans } from './hooks';
import MultiCreditPlanSidePanel from '@/components/ui/CreditPlan/Panels/MultiCreditPlanSidePanel';
import { useAuth } from 'react-oidc-context';
import useCreditTracker from '@/components/ui/CreditTracker/hooks/useCreditTrackerHook';
import { CreditPlanJurisdiction } from '@/components/ui/CreditPlan/util/constants';
import { getCreditPlanRegionImage } from '@/components/ui/CreditPlan/util/CreditPlanHelpers';
import {
  getComplianceModalFormFields,
  getCreditRegionSearchUrl,
  getSpecialStatuses,
  getTransitionOptions,
  periodIsMissingComplianceInformation
} from '@/components/ui/CreditTracker/util/TrackerHelpers';
import {
  selectCreditPlansDebugModeEnabled,
  selectCreditPlanLoadingStepIndex,
  selectCreditPlansGlobalContent
} from '@/redux/selectors/creditPlanSelectors';
import { useAppDispatch, useAppSelector } from '@/hooks';
import CreditPlanFailureSidePanel from '@/components/ui/CreditPlan/Panels/CreditPlanFailureSidePanel';
import {
  useCreateCreditPlanMutation,
  useLazyGetCreditPlanQuery
} from '@/redux/api/client/creditPlan';
import ComplianceModal from '@/components/ui/CreditTracker/Modals/ComplianceModal';
import { datadogRum, DataDogSeverityLevel } from '@/lib/DatadogService';
import { incrementPlanLoadingStepIndex, upsertPlan } from '@/redux/slices/creditPlanSlice';
import { ClientAPI } from '@/redux/api';

export type CreditPlanPanelType = 'multi' | 'single';

const CreditPlanProvider = ({ children }: { children: React.ReactNode }) => {
  const [
    { open, type, jurisdiction, overrideContent, previousType } = { open: false },
    setOpenPlan
  ] = useState<{
    open: boolean;
    type: CreditPlanPanelType;
    previousType?: CreditPlanPanelType;
    jurisdiction?: string;
    overrideContent?: ICreditPlanBlockProps;
  }>();

  const [_, setProviderVersion] = useState<number>(1);
  const [isReloadingPlan, setIsReloadingPlan] = useState<boolean>(false);

  // These need to be separate due to property restrictions of our Modal component setOpen
  const [incompleteTrackerLicense, setIncompleteTrackerLicense] = useState<UserTrackerData>();
  const [incompleteTrackerLicenseModalOpen, setIncompleteTrackerLicenseModalOpen] =
    useState<boolean>(false);

  const auth = useAuth();
  const dispatch = useAppDispatch();
  const globalBlockContent = useAppSelector(selectCreditPlansGlobalContent);
  const debugModeEnabled = useAppSelector(selectCreditPlansDebugModeEnabled);

  const content = useMemo(
    () => overrideContent ?? globalBlockContent,
    [globalBlockContent, overrideContent]
  );

  const [allPlans, { isLoading: isCreditPlansLoading }] = useGetCreditPlans();
  const [openPlan] = useGetCreditPlan(jurisdiction);
  const { creditJurisdictions, licenses, isLoading: isLicensesLoading } = useGetLicenseData();
  const [createPlan] = useCreateCreditPlanMutation();
  const [getPlan] = useLazyGetCreditPlanQuery();

  const openPlanLoadingStepIndex = useAppSelector(state =>
    selectCreditPlanLoadingStepIndex(state, {
      processId: openPlan?.processId ?? undefined,
      licenseIdentifier: openPlan?.licenseIdentifier ?? undefined,
      jurisdiction: openPlan?.jurisdiction ?? jurisdiction ?? undefined
    })
  );

  const user = useMemo(
    () => parseInt(auth.user?.profile?.IndividualSK as string),
    [auth.user?.profile?.IndividualSK]
  );

  const hasPlans = useMemo(() => (allPlans?.length ?? 0) > 0, [allPlans?.length]);
  const creditTrackerArgs = useMemo(
    () => ({
      request: { individualSk: user },
      skipQuery: !auth.isAuthenticated || !hasPlans || !globalBlockContent
    }),
    [auth.isAuthenticated, globalBlockContent, hasPlans, user]
  );
  const { trackerLoading, trackerData } = useCreditTracker(creditTrackerArgs);

  const trackerLicenses = useMemo(() => {
    if (!trackerData) return {};
    return Object.fromEntries(
      trackerData
        .filter(r => r.creditRegionShortDescription)
        ?.map(r => [r.creditRegionShortDescription!.toLowerCase(), r])
    );
  }, [trackerData]);

  const isLoading = useMemo(() => {
    return !!isLicensesLoading || !!trackerLoading || !!isCreditPlansLoading;
  }, [isCreditPlansLoading, isLicensesLoading, trackerLoading]);

  const jurisdictions = useMemo(() => {
    if (isLoading) return [];

    // Limit to MCLE Only
    return creditJurisdictions
      ?.filter(
        j =>
          j.creditClass_SK == 1 &&
          !j.excludeFromCreditPlans &&
          licenses?.items?.find(l => l.creditRegion_SK === j.creditRegion_SK)
      )
      ?.map(j => {
        const plan = allPlans?.find(a => a.jurisdiction?.toLowerCase() == j.id?.toLowerCase());
        const trackerLicense = trackerLicenses[j?.id?.toLowerCase() ?? ''];
        const isTransitionalOrNewlyAdmitted =
          trackerLicense?.jurisdiction?.isTransitionalOrNewlyAdmitted || false;

        const jurisdictionNamePrefix = j?.creditClass ? `${j.creditClass} for ` : '';
        return {
          ...j,
          name: j.name,
          plan: plan?.creditPlan,
          sortOrder: trackerLicense?.baseSortOrder ?? 0,
          shortDescription: `${jurisdictionNamePrefix}${j.stateName}`,
          longDescription: `${jurisdictionNamePrefix}${trackerLicense?.jurisdiction?.displayName ?? trackerLicense?.creditRegionLongDescription}`,
          compliancePeriodEndDate: trackerLicense?.jurisdiction?.clePeriod?.endDate ?? '',
          flags: trackerLicense?.jurisdiction?.activeFlags,
          status: plan?.status,
          isTransitionalOrNewlyAdmitted,
          imagePortrait: getCreditPlanRegionImage(j.id!, isTransitionalOrNewlyAdmitted, 'portrait'),
          imageLandscape: getCreditPlanRegionImage(
            j.id!,
            isTransitionalOrNewlyAdmitted,
            'landscape'
          )
        } as CreditPlanJurisdiction;
      })
      ?.sort((a, b) => a.sortOrder! - b.sortOrder!);
  }, [isLoading, creditJurisdictions, licenses?.items, allPlans, trackerLicenses]);

  const openJurisdiction = useMemo(() => {
    if (!jurisdiction) return null;

    return jurisdictions?.find(j => j.id === jurisdiction) ?? null;
  }, [jurisdictions, jurisdiction]);

  const openJurisdictionBrowseUrl = useMemo(
    () =>
      getCreditRegionSearchUrl(
        openJurisdiction?.id ?? '',
        !!openJurisdiction?.isTransitionalOrNewlyAdmitted
      ),
    [openJurisdiction?.id, openJurisdiction?.isTransitionalOrNewlyAdmitted]
  );

  const showBackButton = useMemo(() => previousType == 'multi', [previousType]);

  /**
   * Deduplicate plans by jurisdiction (prefer completed over failed, latest wins within same status),
   * then sort to guarantee the same order as credit plan block cards.
   */
  const creditPlans = useMemo(() => {
    if (!allPlans) return allPlans;

    const byJurisdiction = new Map<string, CreditPlanResponseModel>();
    for (const plan of allPlans) {
      const key = plan.jurisdiction?.toLowerCase();
      if (!key) continue;
      const existing = byJurisdiction.get(key);
      const planIsCompleted = plan.status === CreditPlanStatus.COMPLETED;
      const existingIsCompleted = existing?.status === CreditPlanStatus.COMPLETED;
      // Prefer completed; among same category, take latest (later in array)
      if (!existing || planIsCompleted || !existingIsCompleted) {
        byJurisdiction.set(key, plan);
      }
    }

    return Array.from(byJurisdiction.values()).sort(
      (a, b) =>
        (jurisdictions?.findIndex(j => j.id == a.jurisdiction) ?? -1) -
        (jurisdictions?.findIndex(j => j.id == b.jurisdiction) ?? -1)
    );
  }, [allPlans, jurisdictions]);

  const complianceModalFormFields = useMemo(
    () => getComplianceModalFormFields(incompleteTrackerLicense),
    [incompleteTrackerLicense]
  );

  const specialStatuses = useMemo(
    () => getSpecialStatuses(getTransitionOptions(incompleteTrackerLicense ?? {})),
    [incompleteTrackerLicense]
  );

  const handleComplianceModalComplete = useCallback(() => {
    // Capture before clearing state
    const jurisdictionId = incompleteTrackerLicense?.creditRegionShortDescription;
    const compliancePeriodEndDate =
      incompleteTrackerLicense?.jurisdiction?.clePeriod?.endDate ?? '';
    const flags = incompleteTrackerLicense?.jurisdiction?.activeFlags ?? [];

    setIncompleteTrackerLicense(undefined);
    setIncompleteTrackerLicenseModalOpen(false);
    dispatch(ClientAPI.util.invalidateTags(['CreditTrackerData']));

    // Bypass validation — compliance was just saved
    setOpenPlan({
      open: true,
      type: 'single',
      jurisdiction: jurisdictionId ?? undefined,
      overrideContent,
      previousType
    });

    createPlan({
      indivSk: auth?.user?.profile?.sub,
      jurisdiction: jurisdictionId!,
      complianceEndDate: compliancePeriodEndDate || new Date().toISOString(),
      flags
    })
      .unwrap()
      .catch(ex => {
        datadogRum.addError(ex);
        dispatch(
          upsertPlan({
            jurisdiction: jurisdictionId ?? '',
            plan: {
              jurisdiction: jurisdictionId,
              status: CreditPlanStatus.FAILED,
              failureReason: CreditPlanFailureReason.UNABLE_TO_START_PLAN_GENERATION
            }
          })
        );
      });
  }, [
    incompleteTrackerLicense,
    overrideContent,
    previousType,
    dispatch,
    createPlan,
    auth?.user?.profile?.sub
  ]);

  const validateTrackerComplianceSetupStatus = useCallback(
    (j: string) => {
      const license = trackerLicenses[j?.toLowerCase()];
      if (license?.jurisdiction) {
        const result = periodIsMissingComplianceInformation(
          license?.jurisdiction?.clePeriod,
          license?.jurisdiction?.requireSetup
        );

        const hasActivePlan = creditPlans?.some(a => a.jurisdiction == j);
        const isOpen = result && !hasActivePlan;

        if (isOpen) {
          setIncompleteTrackerLicense(license);
          setIncompleteTrackerLicenseModalOpen(true);
          return false;
        }
      }
      return true;
    },
    [creditPlans, trackerLicenses]
  );

  const createCreditPlan = useCallback(
    (jurisdiction: CreditPlanJurisdiction) => {
      if (!jurisdiction?.id) {
        datadogRum.addError(
          new Error('[createCreditPlan] Missing jurisdiction id while creating plan.'),
          {
            severityLevel: DataDogSeverityLevel.Warning
          }
        );
        return;
      }

      if (!validateTrackerComplianceSetupStatus(jurisdiction?.id)) return;

      createPlan({
        indivSk: auth?.user?.profile?.sub,
        jurisdiction: jurisdiction.id!,
        complianceEndDate: jurisdiction.compliancePeriodEndDate || new Date().toISOString(),
        flags: jurisdiction.flags
      })
        .unwrap()
        .then(_response => {
          console.info('Credit plan create started successfully, response:', _response);
          // Redux slice upserts initial credit plan response.
          // SignalR connection will upsert further updates.
        })
        .catch(ex => {
          datadogRum.addError(ex);
          dispatch(
            upsertPlan({
              jurisdiction: jurisdiction.id ?? '',
              plan: {
                jurisdiction: jurisdiction.id,
                status: CreditPlanStatus.FAILED,
                failureReason: CreditPlanFailureReason.UNABLE_TO_START_PLAN_GENERATION
              }
            })
          );
          setOpenPlan({
            open: true,
            type: 'single',
            jurisdiction: jurisdiction.id ?? undefined,
            overrideContent,
            previousType: previousType
          });
        });
    },
    [
      auth?.user?.profile?.sub,
      createPlan,
      dispatch,
      overrideContent,
      previousType,
      validateTrackerComplianceSetupStatus
    ]
  );

  const openPanel = useCallback(
    (
      jurisdiction?: string | null,
      overrideContent?: ICreditPlanBlockProps,
      previousType?: CreditPlanPanelType
    ) => {
      setOpenPlan({
        open: validateTrackerComplianceSetupStatus(jurisdiction ?? ''),
        type: jurisdiction || creditPlans?.length == 1 ? 'single' : 'multi',
        jurisdiction: jurisdiction ?? undefined,
        overrideContent,
        previousType: previousType
      });
    },
    [creditPlans?.length, validateTrackerComplianceSetupStatus]
  );

  function handleClose() {
    // keep the previous state for smoother closing animation (content doesn't get lost)
    setOpenPlan(prev => ({ ...prev!, open: false, previousType: undefined }));
  }

  function handlePanelBackButton() {
    // keep the previous state for smoother closing animation (content doesn't get lost)
    setOpenPlan(prev => ({
      open: true,
      type: 'multi',
      overrideContent: prev?.overrideContent,
      previousType: undefined
    }));
  }

  const getCreditPlan = useCallback(async () => {
    if (openPlan?.processId) {
      setIsReloadingPlan(true);
      const { isError, isSuccess, data } = await getPlan(openPlan.processId);
      setIsReloadingPlan(false);

      if (isSuccess) {
        setOpenPlan(prev => ({
          open: true,
          type: 'single',
          jurisdiction: prev?.jurisdiction ?? data?.jurisdiction ?? '',
          overrideContent: prev?.overrideContent
        }));
      } else if (isError) {
        datadogRum.addError(new Error('[getCreditPlan] Failed to refetch single credit plan.'), {
          severityLevel: DataDogSeverityLevel.Error
        });
      }
      setProviderVersion(prev => prev + 1);
    }
  }, [getPlan, openPlan?.processId]);

  const advanceOpenPlanLoadingStep = useCallback(
    (stepCount: number) => {
      dispatch(
        incrementPlanLoadingStepIndex({
          processId: openPlan?.processId ?? undefined,
          licenseIdentifier: openPlan?.licenseIdentifier ?? undefined,
          jurisdiction: openPlan?.jurisdiction ?? jurisdiction ?? '',
          stepCount
        })
      );
    },
    [
      dispatch,
      openPlan?.licenseIdentifier,
      openPlan?.processId,
      openPlan?.jurisdiction,
      jurisdiction
    ]
  );

  return (
    <creditPlanContext.Provider
      value={{
        openPanel,
        handlePanelBackButton,
        createCreditPlan,
        jurisdictions,
        isLoading,
        creditPlans,
        isPanelOpen: open ?? false,
        closePanel: handleClose
      }}
    >
      {children}

      {/* View the credit plan */}
      <GeneratedCreditPlanSidePanel
        isOpen={
          open &&
          type === 'single' &&
          !!openPlan &&
          (openPlan.status == CreditPlanStatus.COMPLETED ||
            (openPlan.status == CreditPlanStatus.FAILED &&
              openPlan.failureReason == CreditPlanFailureReason.GENERAL))
        }
        onOpenChange={handleClose}
        jurisdictionId={openJurisdiction?.id ?? undefined}
        jurisdictionName={openJurisdiction?.longDescription ?? undefined}
        {...content}
        content={{
          ...content?.singlePlanSidePanelContent,
          alerts: openPlan?.alertMessages ?? []
        }}
        debugModeEnabled={debugModeEnabled}
        onBackButtonPress={showBackButton ? handlePanelBackButton : undefined}
        creditPlanLoadFailureContent={content?.creditPlanLoadFailure}
        openJurisdictionBrowseUrl={openJurisdictionBrowseUrl}
      />

      {/* View the execution process */}
      <ExecutingCreditPlanSidePanel
        isOpen={
          open &&
          type === 'single' &&
          (!openPlan ||
            [CreditPlanStatus.EXECUTING, CreditPlanStatus.STARTED].includes(openPlan.status!))
        }
        onOpenChange={handleClose}
        content={content?.executingSidePanelContent}
        onBackButtonPress={showBackButton ? handlePanelBackButton : undefined}
        loadingStepIndex={openPlanLoadingStepIndex}
        onAdvanceLoadingStepIndex={advanceOpenPlanLoadingStep}
      />

      {/* Select from multiple credit plans */}
      <MultiCreditPlanSidePanel
        isOpen={open && type === 'multi' && creditPlans?.length > 1}
        onOpenChange={handleClose}
        content={content?.multiPlanSidePanelContent}
        openPanel={openPanel}
        jurisdictions={jurisdictions}
        isLoading={isLoading}
        creditPlans={creditPlans}
      />

      {/* Show a failure panel */}
      <CreditPlanFailureSidePanel
        isOpen={open && type === 'single' && openPlan?.status == CreditPlanStatus.FAILED}
        onOpenChange={handleClose}
        jurisdictionName={openJurisdiction?.longDescription ?? undefined}
        failureReason={openPlan?.failureReason}
        debugInformation={openPlan?.debugInformation}
        debugModeEnabled={debugModeEnabled}
        openJurisdictionBrowseUrl={openJurisdictionBrowseUrl}
        planId={openPlan?.processId ?? undefined}
        handleBackButton={showBackButton ? handlePanelBackButton : undefined}
        creditPlanIncompleteFailureContent={content?.creditPlanIncompleteFailure}
        creditPlanLoadFailureContent={content?.creditPlanLoadFailure}
        isLoading={isReloadingPlan}
        createCreditPlan={() => {
          setProviderVersion(prev => prev + 1);
          const retryJurisdiction =
            openJurisdiction ??
            jurisdictions?.find(
              j => j.id === (openPlan?.jurisdiction ?? jurisdiction ?? '').toString()
            );

          if (!retryJurisdiction?.id) {
            datadogRum.addError(
              new Error(
                '[CreditPlanFailureSidePanel] Unable to resolve jurisdiction for create retry.'
              ),
              {
                severityLevel: DataDogSeverityLevel.Warning
              }
            );
            return;
          }

          createCreditPlan(retryJurisdiction);
        }}
        getCreditPlan={() => {
          datadogRum.addAction('Retry Get Credit Plan', {
            Jurisdiction: ''
          });
          getCreditPlan();
        }}
      />

      {/* Compliance Information Form Modal */}
      {!!incompleteTrackerLicenseModalOpen && (
        <ComplianceModal
          customerSk={user}
          isMissingCompliancePeriod={true}
          key={`${incompleteTrackerLicense?.creditRegionShortDescription}-complianceModal`}
          open={!!incompleteTrackerLicenseModalOpen}
          complianceGroups={incompleteTrackerLicense?.complianceGroups}
          jurisdiction={incompleteTrackerLicense?.jurisdiction}
          eyebrowText={incompleteTrackerLicense?.creditRegionLongDescription ?? ''}
          selectedPeriod={'current'}
          complianceFormFields={complianceModalFormFields}
          setOpen={setIncompleteTrackerLicenseModalOpen}
          specialStatuses={specialStatuses}
          onComplete={handleComplianceModalComplete}
        />
      )}
    </creditPlanContext.Provider>
  );
};
export default CreditPlanProvider;
